import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Scale, MapPin, Save, TrendingDown, TrendingUp, Minus, AlertTriangle, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Toggle } from './ui/toggle';
import axios from 'axios';
import { enqueue, isOfflineError } from '../utils/offlineStore';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEIGHT_LOSS_REASONS = ['Robbing', 'Starvation', 'Swarm Prep', 'Disease', 'Seasonal Decline', 'Harvested Honey', 'Absconding', 'Varroa', 'Weather', 'Other'];

const HEFT_OPTIONS = [
  { value: 'Light', color: 'data-[state=on]:bg-red-600 data-[state=on]:text-white bg-red-50 text-red-700' },
  { value: 'Medium', color: 'data-[state=on]:bg-amber-600 data-[state=on]:text-white bg-amber-50 text-amber-700' },
  { value: 'Heavy', color: 'data-[state=on]:bg-green-600 data-[state=on]:text-white bg-green-50 text-green-700' },
];

const HEFT_COLOR = { Light: 'text-red-600', Medium: 'text-amber-600', Heavy: 'text-green-600' };

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return '—'; } };

const BulkWeightEntry = ({ hives = [], yards = [], inspections = [] }) => {
  const [selectedYard, setSelectedYard] = useState('all');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [weights, setWeights] = useState({});
  const [heftWeights, setHeftWeights] = useState({});
  const [notes, setNotes] = useState({});
  const [weightLossReasons, setWeightLossReasons] = useState({});
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [unitPreference, setUnitPreference] = useState('kg');
  const [refreshKey, setRefreshKey] = useState(0); // triggers re-fetch of last weights

  // Load unit preference
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/weight-settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { if (res.data?.unit_preference) setUnitPreference(res.data.unit_preference); })
        .catch(() => {});
    }
  }, []);

  // Filter active hives by yard
  const filteredHives = useMemo(() => {
    let result = hives.filter(h => h.status === 'active');
    if (selectedYard && selectedYard !== 'all') {
      result = result.filter(h => h.yard === selectedYard);
    }
    return result.sort((a, b) => (a.hive_number || 0) - (b.hive_number || 0));
  }, [hives, selectedYard]);

  // Get last weight + heft + date for each hive from the latest-inspections API
  const [lastWeightData, setLastWeightData] = useState({}); // { hiveId: { weight, heft, date } }

  const fetchLastWeights = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/hives/latest-inspections`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data || {};
      const map = {};
      for (const [hiveId, info] of Object.entries(data)) {
        map[hiveId] = {
          weight: info.weight ? parseFloat(info.weight) : null,
          heft: info.heft_weight || '',
          date: info.date || '',
        };
      }
      setLastWeightData(map);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchLastWeights();
  }, [fetchLastWeights, refreshKey]);

  // Also rebuild from inspections prop as fallback (for weight change calculation)
  const lastWeights = useMemo(() => {
    const map = {};
    for (const hive of filteredHives) {
      const d = lastWeightData[hive.id];
      if (d?.weight) map[hive.id] = d.weight;
    }
    return map;
  }, [filteredHives, lastWeightData]);

  const getChange = (hiveId) => {
    const current = parseFloat(weights[hiveId]);
    const last = lastWeights[hiveId];
    if (!current || !last) return null;
    return current - last;
  };

  const handleSave = async () => {
    const entries = [];
    const allHiveIds = new Set([...Object.keys(weights), ...Object.keys(heftWeights)]);
    for (const hiveId of allHiveIds) {
      const w = weights[hiveId];
      const heft = heftWeights[hiveId];
      const hasWeight = w && parseFloat(w) > 0;
      const hasHeft = heft && heft.length > 0;
      if (hasWeight || hasHeft) {
        entries.push({
          hive_id: hiveId,
          weight: hasWeight ? parseFloat(w) : null,
          heft_weight: heft || '',
          notes: notes[hiveId] || '',
          weight_loss_reasons: weightLossReasons[hiveId] || []
        });
      }
    }

    if (entries.length === 0) {
      toast.error('Enter at least one weight or heft assessment');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/hives/bulk-weight-entry`, {
        entries,
        date,
        session_notes: sessionNotes
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(res.data.message);
      setWeights({});
      setHeftWeights({});
      setNotes({});
      setWeightLossReasons({});
      setSessionNotes('');
      // Refresh both parent data and local last-weight data
      window.dispatchEvent(new Event('refreshData'));
      setRefreshKey(k => k + 1);
    } catch (err) {
      if (isOfflineError(err)) {
        await enqueue({
          kind: 'weights',
          path: '/hives/bulk-weight-entry',
          body: { entries, date, session_notes: sessionNotes },
          label: `Weights · ${entries.length} hive${entries.length > 1 ? 's' : ''}`,
          hiveIds: entries.map(e => e.hive_id)
        });
        toast.success('No signal: weights saved on this phone', { description: 'They will upload automatically when you are back in coverage.' });
        setWeights({});
        setHeftWeights({});
        setNotes({});
        setWeightLossReasons({});
        setSessionNotes('');
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to save weights');
    } finally {
      setSaving(false);
    }
  };

  const enteredCount = (() => {
    const allHiveIds = new Set([...Object.keys(weights), ...Object.keys(heftWeights)]);
    let count = 0;
    for (const hiveId of allHiveIds) {
      const hasWeight = weights[hiveId] && parseFloat(weights[hiveId]) > 0;
      const hasHeft = heftWeights[hiveId] && heftWeights[hiveId].length > 0;
      if (hasWeight || hasHeft) count++;
    }
    return count;
  })();

  return (
    <div className="space-y-4" data-testid="bulk-weight-entry">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Scale className="h-6 w-6 text-amber-600" />
            Record Weights
          </h2>
          <p className="text-sm text-stone-500 mt-0.5">Quickly enter weights for all hives in a yard</p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border border-stone-200 rounded-lg text-sm"
            data-testid="bulk-weight-date"
          />
          <Select value={selectedYard} onValueChange={setSelectedYard}>
            <SelectTrigger className="w-[180px] h-10" data-testid="bulk-weight-yard-select">
              <SelectValue placeholder="Select Yard" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Yards</SelectItem>
              {yards.map(y => (
                <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Weight Trends Summary */}
      {filteredHives.length > 0 && Object.keys(lastWeights).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="weight-trends-summary">
          {(() => {
            const hivesWithWeight = filteredHives.filter(h => lastWeights[h.id]);
            const allWeights = hivesWithWeight.map(h => lastWeights[h.id]);
            const avg = allWeights.length > 0 ? (allWeights.reduce((s, w) => s + w, 0) / allWeights.length) : 0;
            const lightest = hivesWithWeight.reduce((min, h) => (!min || lastWeights[h.id] < lastWeights[min.id]) ? h : min, null);
            const heaviest = hivesWithWeight.reduce((max, h) => (!max || lastWeights[h.id] > lastWeights[max.id]) ? h : max, null);
            const totalWeight = allWeights.reduce((s, w) => s + w, 0);

            return (
              <>
                <Card className="p-3 bg-amber-50 border-amber-200">
                  <div className="text-xs text-amber-600 font-medium">Yard Average</div>
                  <div className="text-xl font-bold text-amber-800">{avg.toFixed(1)} {unitPreference}</div>
                  <div className="text-xs text-amber-500">{hivesWithWeight.length} hive{hivesWithWeight.length !== 1 ? 's' : ''} weighed</div>
                </Card>
                <Card className="p-3 bg-green-50 border-green-200">
                  <div className="text-xs text-green-600 font-medium">Heaviest</div>
                  <div className="text-xl font-bold text-green-800">{heaviest ? lastWeights[heaviest.id].toFixed(1) : '—'} {unitPreference}</div>
                  <div className="text-xs text-green-500">{heaviest ? `Hive #${heaviest.hive_number}` : '—'}</div>
                </Card>
                <Card className="p-3 bg-blue-50 border-blue-200">
                  <div className="text-xs text-blue-600 font-medium">Lightest</div>
                  <div className="text-xl font-bold text-blue-800">{lightest ? lastWeights[lightest.id].toFixed(1) : '—'} {unitPreference}</div>
                  <div className="text-xs text-blue-500">{lightest ? `Hive #${lightest.hive_number}` : '—'}</div>
                </Card>
                <Card className="p-3 bg-stone-50 border-stone-200">
                  <div className="text-xs text-stone-600 font-medium">Total Yard Weight</div>
                  <div className="text-xl font-bold text-stone-800">{totalWeight.toFixed(1)} {unitPreference}</div>
                  <div className="text-xs text-stone-500">{allWeights.length} of {filteredHives.length} tracked</div>
                </Card>
              </>
            );
          })()}
        </div>
      )}

      {/* Weight Entry Table */}
      {filteredHives.length === 0 ? (
        <Card className="text-center p-8">
          <Scale className="h-12 w-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No active hives{selectedYard !== 'all' ? ` in ${selectedYard}` : ''}. Select a yard with hives.</p>
        </Card>
      ) : (
        <>
          {/* Heft Legend */}
          <div className="flex items-center gap-3 text-[11px] text-stone-500 bg-stone-50 px-3 py-2 rounded-lg border border-stone-200" data-testid="heft-legend">
            <span className="font-semibold text-stone-600">Heft Guide:</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1" />L = Light (&lt;25kg) — needs feeding</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />M = Medium (25–40kg) — monitor</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />H = Heavy (&gt;40kg) — well provisioned</span>
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Hive</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Last Weight</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">New Weight ({unitPreference})</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Heft</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Change</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredHives.map((hive) => {
                    const change = getChange(hive.id);
                    const lastW = lastWeights[hive.id];
                    const d = lastWeightData[hive.id] || {};
                    const lastHeft = d.heft || '';
                    const lastDate = d.date ? fmtDate(d.date) : '';
                    return (
                      <React.Fragment key={hive.id}>
                      <tr className="hover:bg-amber-50/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-stone-800">
                            #{hive.hive_number}
                            {hive.nickname && <span className="text-stone-400 font-normal ml-1">"{hive.nickname}"</span>}
                          </div>
                          {hive.yard && (
                            <div className="text-xs text-stone-400 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />{hive.yard}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {lastW ? (
                            <div>
                              <span className="font-medium text-stone-700">{lastW} {unitPreference}</span>
                              {lastHeft && <span className={`ml-1.5 text-xs font-semibold ${HEFT_COLOR[lastHeft] || 'text-stone-500'}`}>({lastHeft})</span>}
                              {lastDate && <div className="text-[10px] text-stone-400 flex items-center gap-0.5 mt-0.5"><Calendar className="h-2.5 w-2.5" />{lastDate}</div>}
                            </div>
                          ) : lastHeft ? (
                            <div>
                              <span className={`font-semibold text-sm ${HEFT_COLOR[lastHeft] || 'text-stone-500'}`}>{lastHeft}</span>
                              {lastDate && <div className="text-[10px] text-stone-400 flex items-center gap-0.5 mt-0.5"><Calendar className="h-2.5 w-2.5" />{lastDate}</div>}
                            </div>
                          ) : (
                            <span className="text-stone-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={weights[hive.id] || ''}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setWeights(prev => ({ ...prev, [hive.id]: newVal }));
                              const newWeight = parseFloat(newVal);
                              const lastW = lastWeights[hive.id];
                              if (lastW && newWeight >= lastW) {
                                setWeightLossReasons(prev => { const next = { ...prev }; delete next[hive.id]; return next; });
                              }
                            }}
                            placeholder="0.0"
                            className="w-24 px-2 py-1.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-300 focus:border-amber-400 outline-none"
                            data-testid={`weight-input-${hive.id}`}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1">
                            {HEFT_OPTIONS.map(opt => (
                              <Toggle
                                key={opt.value}
                                pressed={heftWeights[hive.id] === opt.value}
                                onPressedChange={() => setHeftWeights(prev => ({ ...prev, [hive.id]: prev[hive.id] === opt.value ? '' : opt.value }))}
                                className={`h-8 px-2 text-[10px] font-semibold ${opt.color}`}
                                data-testid={`heft-${hive.id}-${opt.value.toLowerCase()}`}
                              >
                                {opt.value[0]}
                              </Toggle>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {change !== null ? (
                            <span className={`text-sm font-medium flex items-center gap-1 ${
                              change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-stone-400'
                            }`}>
                              {change > 0 ? <TrendingUp className="h-3.5 w-3.5" /> :
                               change < 0 ? <TrendingDown className="h-3.5 w-3.5" /> :
                               <Minus className="h-3.5 w-3.5" />}
                              {change > 0 ? '+' : ''}{change.toFixed(1)} {unitPreference}
                              {change < -2 && <AlertTriangle className="h-3.5 w-3.5 text-red-500 ml-1" />}
                            </span>
                          ) : (
                            <span className="text-stone-300 text-sm">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={notes[hive.id] || ''}
                            onChange={(e) => setNotes(prev => ({ ...prev, [hive.id]: e.target.value }))}
                            placeholder="Optional"
                            className="w-full max-w-[150px] px-2 py-1.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-300 focus:border-amber-400 outline-none"
                          />
                        </td>
                      </tr>
                      {/* Weight decrease reasons row */}
                      {change !== null && change < 0 && (
                        <tr className="bg-red-50/50">
                          <td colSpan="6" className="px-4 py-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-xs font-medium text-red-700 mr-1">Possible reasons:</span>
                              {WEIGHT_LOSS_REASONS.map((reason) => {
                                const selected = (weightLossReasons[hive.id] || []).includes(reason);
                                return (
                                  <button
                                    key={reason}
                                    type="button"
                                    onClick={() => {
                                      setWeightLossReasons(prev => {
                                        const current = prev[hive.id] || [];
                                        const updated = current.includes(reason)
                                          ? current.filter(r => r !== reason)
                                          : [...current, reason];
                                        return { ...prev, [hive.id]: updated };
                                      });
                                    }}
                                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                      selected
                                        ? 'bg-red-600 text-white border-red-600'
                                        : 'bg-white text-stone-600 border-stone-300 hover:border-red-300'
                                    }`}
                                    data-testid={`weight-reason-${hive.id}-${reason.toLowerCase().replace(/\s/g, '-')}`}
                                  >
                                    {reason}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Session Notes + Save */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1">
              <label className="text-sm font-medium text-stone-700">Session Notes (optional)</label>
              <input
                type="text"
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                placeholder="e.g., After rain, morning weigh-in"
                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm"
                data-testid="bulk-weight-session-notes"
              />
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || enteredCount === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white px-6 h-10 whitespace-nowrap"
              data-testid="bulk-weight-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : `Save ${enteredCount} Weight${enteredCount !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkWeightEntry;
